import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { authActions } from 'core/actions/auth'
import { RouteComponentProps } from 'react-router'
import { Form, Input, Button, FormInstance } from 'antd'
import { Link } from 'react-router-dom'
import Icon from '../../elements/Icon'

interface InviteRouteParams {
  id: string
  token: string
}

let firstTimeLogin = +new Date()
let retryLogin: NodeJS.Timeout | undefined = undefined

function LogsLogin({ match }: RouteComponentProps<InviteRouteParams>): JSX.Element {
  const [loggingIn] = useState(false)
  const [message, setMessage] = useState('')
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { getFieldError, isFieldTouched } = form
  const usernameError = isFieldTouched('username') && getFieldError('username')
  const passwordError = isFieldTouched('password') && getFieldError('password')

  const handleSubmit = async (values: { [key: string]: string }) => {
    try {
      await authActions.attemptLogin(values.username, values.password, '/dashboard')(dispatch)
    } catch (e) {
      setMessage(t('Login.login-failed'))
    }
  }

  function attemptLoginFromInvitation() {
    retryLogin = undefined
    authActions
      .attemptLogin(
        match.params.id,
        match.params.token,
        '/dashboard',
      )(dispatch)
      .catch((e) => {
        if (+new Date() - firstTimeLogin < 120000) {
          setMessage(t('Login.retry-login'))
          retryLogin && clearTimeout(retryLogin)
          retryLogin = setTimeout(() => attemptLoginFromInvitation(), 3000)
        } else {
          setMessage(t('Login.login-failed'))
        }
        console.log(e)
      })
  }

  function hasErrors(form: FormInstance) {
    return !!form.getFieldsError().filter(({ errors }) => errors.length).length
  }

  useEffect(() => {
    if (match.params.id && match.params.token) {
      firstTimeLogin = +new Date()
      attemptLoginFromInvitation()
    }
  }, [match])

  return (
    <div className="log-wrap">
      <Link to="/" className="log__backlink">
        <Icon name="arrow-left" />
      </Link>
      <div id="robot">
        <svg xmlns="http://www.w3.org/2000/svg" width="330" height="360" viewBox="0 0 330 360">
          <g fill="none" fillRule="evenodd">
            <g fillRule="nonzero">
              <g>
                <g transform="translate(-574 -4124) translate(573.729 4105) rotate(-29 238.495 60.372)">
                  <path
                    fill="#F16062"
                    d={
                      // eslint-disable-next-line max-len
                      'M109.967 118.03c-8.886 0-17.01 1.78-24.626 5.088-7.616 3.307-13.963 7.886-19.548 13.737-5.585 5.851-9.901 12.466-12.948 20.352-3.046 7.886-4.823 16.281-4.823 25.185 0 9.159 1.523 17.554 4.823 25.694 3.047 7.887 7.617 14.755 13.202 20.606 5.585 5.852 12.44 10.43 20.31 13.738 7.87 3.307 16.248 5.088 25.387 5.088 12.694 0 23.864-3.053 33.511-9.159 9.648-6.105 17.264-14.246 23.357-24.676l36.811 25.694c-10.154 16.536-22.848 29.256-38.08 38.414-15.233 9.158-33.512 13.737-54.837 13.737-15.994 0-30.719-2.798-44.428-8.395-13.71-5.597-25.641-13.228-35.542-23.15-9.902-9.921-17.772-21.37-23.61-34.598C3.34 212.157.293 197.91.293 182.647c0-15.264 2.792-29.51 8.631-42.484 5.585-12.975 13.456-24.423 23.357-34.344 9.9-9.667 21.579-17.299 35.034-22.896 13.456-5.596 28.18-8.395 44.174-8.395 20.564 0 38.589 4.834 54.583 12.72 15.232 7.632 35.035 20.097 47.22 48.844l-37.319 25.949c-6.093-10.176-22.34-27.984-31.48-34.344-9.901-6.614-21.072-9.667-34.527-9.667z'
                    }
                  />
                  <g>
                    <path fill="#08928F" d="M9.5 53.831L9.5 0" transform="translate(102.033 48.599)" />
                    <path fill="#F16062" d="M0 0H19V53.831H0z" transform="translate(102.033 48.599)" />
                  </g>
                  <circle cx="110.92" cy="28.104" r="28" fill="#F16062" />
                  <g transform="translate(70.491 144.097)">
                    <g>
                      <ellipse cx="65.332" cy="19.337" fill="#3F3F3E" rx="18.647" ry="18.896" />
                      <g>
                        <ellipse cx="18.842" cy="19.337" fill="#3F3F3E" rx="18.647" ry="18.896" />
                        <ellipse cx="23.696" cy="8.868" fill="#FFF" rx="4.853" ry="4.852" />
                      </g>
                    </g>
                    <ellipse cx="72.484" cy="8.868" fill="#FFF" rx="4.853" ry="4.852" />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
      <div id="container">
        <h1>Logs Explorer Login</h1>
        <Form form={form} onFinish={(values) => handleSubmit(values)} className="login-form" colon={false}>
          <Form.Item
            label={t('Login.nom-utilisateur')}
            name="username"
            validateStatus={usernameError ? 'error' : ''}
            help={usernameError || ''}
            rules={[
              {
                required: true,
                message: t('Login.veuillez-introduire-votre-identifiant'),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('Login.mot-passe')}
            validateStatus={passwordError ? 'error' : ''}
            help={passwordError || ''}
            name="password"
            rules={[
              {
                required: true,
                message: t('Login.veuillez-introduire-votre-mot-passe'),
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <div className="d-flex justify-content-center mt-4">
            <Button htmlType="submit" className="blue xl" disabled={hasErrors(form) || loggingIn}>
              Login
            </Button>
          </div>
        </Form>
      </div>

      <div className="log-aside"></div>
    </div>
  )
}
export default LogsLogin
